import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  styled,
  TextField,
} from "@material-ui/core";
import type { EpocusPerformerCredentialStatus } from "elephant-web/gql";
import { useEffect, useState } from "react";
import { rem } from "../../../_lib/epocus-palette";
import type { DirectoryMember } from "../../../_lib/use-program-directory";
import { useSetEpocusPerformerCredentialMutation } from "./__generated__/credentialing-operations.gql";
import {
  CREDENTIAL_STATUS_OPTIONS,
  credentialStatusLabel,
} from "./credential-status";

export type EditableCredential = {
  performerId: string;
  performerName: string;
  status: EpocusPerformerCredentialStatus;
  supervisorId: string | null;
  supervisorName: string | null;
  unit: string | null;
};

const Fields = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  paddingTop: 8,
});

const NO_SUPERVISOR = "";

export const EditCredentialSheet: FC<{
  programId: string;
  credential: EditableCredential | null;
  supervisors: readonly DirectoryMember[];
  onClose: () => void;
}> = ({ programId, credential, supervisors, onClose }) => {
  const [status, setStatus] = useState<EpocusPerformerCredentialStatus | "">(
    "",
  );
  const [supervisorId, setSupervisorId] = useState(NO_SUPERVISOR);
  const [unit, setUnit] = useState("");

  useEffect(() => {
    if (!credential) return;
    setStatus(credential.status);
    setSupervisorId(credential.supervisorId ?? NO_SUPERVISOR);
    setUnit(credential.unit ?? "");
  }, [credential]);

  /*
   * A supervisor who has since left the program is not in `supervisors`, and a
   * MUI select renders an unmatched value as blank — which reads as "none", so
   * saving an unrelated field would quietly clear a supervisor still on record.
   * Carrying them as an extra option keeps them visible and re-savable.
   */
  const options =
    credential?.supervisorId &&
    !supervisors.some(({ id }) => id === credential.supervisorId)
      ? [
          {
            id: credential.supervisorId,
            name: credential.supervisorName ?? "Supervisor (no longer listed)",
          },
          ...supervisors,
        ]
      : supervisors;

  const [save, { loading }] = useSetEpocusPerformerCredentialMutation({
    onCompleted: () => {
      toast.success("Credential updated");
      onClose();
    },
    onError: (error) =>
      toast.error(`Couldn't update the credential: ${error.message}`),
    refetchQueries: ["EpocusProgramCredentialing"],
  });

  const submit = () => {
    if (!credential || status === "") return;
    /*
     * Every field goes on the input, including the ones left untouched: the
     * mutation writes the whole credential, so omitting a cleared supervisor
     * would keep the one the admin believes they just removed.
     */
    save({
      variables: {
        input: {
          programId,
          performerId: credential.performerId,
          status,
          supervisorId: supervisorId === NO_SUPERVISOR ? null : supervisorId,
          unit: unit.trim() === "" ? null : unit.trim(),
        },
      },
    });
  };

  return (
    <Dialog
      open={credential !== null}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{credential?.performerName ?? "Performer"}</DialogTitle>
      <DialogContent>
        <Fields>
          <TextField
            select
            id="credential-status"
            label="Credential status"
            size="small"
            variant="outlined"
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as EpocusPerformerCredentialStatus)
            }
          >
            {CREDENTIAL_STATUS_OPTIONS.map((option) => (
              <MenuItem
                key={option}
                value={option}
                style={{ fontSize: rem(13) }}
              >
                {credentialStatusLabel(option)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            id="credential-supervisor"
            label="Supervisor"
            size="small"
            variant="outlined"
            value={supervisorId}
            onChange={(event) => setSupervisorId(event.target.value)}
            helperText="No supervisor means none is required."
          >
            <MenuItem value={NO_SUPERVISOR} style={{ fontSize: rem(13) }}>
              No supervisor
            </MenuItem>
            {options.map(({ id, name }) => (
              <MenuItem key={id} value={id} style={{ fontSize: rem(13) }}>
                {name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="credential-unit"
            label="Unit"
            size="small"
            variant="outlined"
            value={unit}
            onChange={(event) => setUnit(event.target.value)}
          />
        </Fields>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || status === ""}
          onClick={submit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
